import { useLazyQuery, useQuery } from '@apollo/client/react';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CircularProgress,
  Autocomplete as MUIAutocomplete,
  TextField,
} from '@mui/material';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import {
  SelfDocument,
  UserAutocompleteDetailsFragment,
  UserAutocompleteDocument,
  UserBasicsFragment,
} from '../../../graphql/types';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import {
  BORDER_GRAY,
  COOL_GRAY100,
  ICON_SECONDARY,
  TEXT_DISABLED,
  WHITE,
} from '../../../theme';
import { getMemberName } from '../../../utils/getMemberName';
import { EventLabels, EventNames } from '../../../utils/trackingEvents';
import Avatar from '../Avatar';
import { ButtonSize, ButtonVariant } from '../Button';
import Errors from '../ErrorMessage';
import { Link } from '../Link';
import Text, { TextColor, TextKind, textStyleFrom } from '../Text';
import Tooltip from '../Tooltip';

const StyledAutoCompleteContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledMuiAutocomplete: typeof MUIAutocomplete = styled(MUIAutocomplete)`
  .MuiAutocomplete-inputRoot {
    padding: 0;
  }
  fieldset {
    border: none !important;
  }
`;

const TextInputFontStyles = textStyleFrom(TextKind.BodyM, TextColor.Primary);

const StyledMuiTextField = styled(TextField)`
  border-radius: 8px !important;
  border: 1px solid ${BORDER_GRAY} !important;
  background: ${WHITE};
  box-sizing: border-box;

  && .MuiOutlinedInput-root {
    padding: 0;
  }
  input {
    padding: 8px !important;

    & {
      ${{ ...TextInputFontStyles }}
    }
    &::placeholder,
    &::-webkit-input-placeholder {
      color: ${ICON_SECONDARY};
      opacity: 1;
    }
  }

  .Mui-disabled {
    cursor: default;

    color: ${TEXT_DISABLED} !important;
    background: ${COOL_GRAY100};
    -webkit-text-fill-color: ${TEXT_DISABLED} !important; // really?
  }
`;

interface MemberAutoCompleteProps {
  users: UserBasicsFragment[];
  onInputChange?: (value: string) => void;
  onSelection: (user: UserAutocompleteDetailsFragment | null) => void;
  selectedUserId: string | null;
  loading?: boolean;
  name?: string;
  trackingData?: TrackingEventData;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  showViewProfileButton?: boolean;
}

export const MemberAutocomplete: React.FC<MemberAutoCompleteProps> = ({
  users,
  onInputChange,
  loading,
  onSelection,
  selectedUserId,
  name,
  trackingData,
  label,
  placeholder,
  disabled,
  showViewProfileButton = true,
}) => {
  const { track } = useTracking();
  const { data: selfData } = useQuery(SelfDocument);
  const selfId = selfData?.self?.id;

  // we need to fetch for the selected user if the selected user already exists
  // but does not exist in provided `users` filtered/paginated list
  // for cases where we render the autocomplete directly with a pre-selected user (ie: add session from member profile)
  const [fetch, { data }] = useLazyQuery(UserAutocompleteDocument, {
    fetchPolicy: 'cache-first',
  });

  const selectedUserInList = users.find((user) => user.id === selectedUserId);

  useEffect(() => {
    if (selectedUserId && !selectedUserInList) {
      fetch({
        variables: {
          id: selectedUserId,
        },
      });
    }
  }, [selectedUserId, fetch, selectedUserInList]);

  const selectedUser = ((): UserAutocompleteDetailsFragment | null => {
    if (!selectedUserId) {
      return null;
    }

    if (selectedUserInList) {
      return {
        firstName: selectedUserInList.firstName,
        lastName: selectedUserInList.lastName,
        avatar: selectedUserInList.avatar,
        id: selectedUserInList.id,
      };
    }

    if (data?.user) {
      return data?.user;
    }

    return null;
  })();

  const handleFocus = () => {
    if (trackingData) {
      track(EventNames.DropDownMenuFocus, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
  };
  const handleOpen = () => {
    if (trackingData) {
      track(EventNames.DropDownMenuClick, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
  };
  const handleSelection = (
    newValue: UserAutocompleteDetailsFragment | null,
  ) => {
    if (trackingData && newValue) {
      track(EventNames.DropDownMenuItemClick, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
    if (trackingData && !newValue) {
      track(EventNames.DropDownMenuItemClear, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
    onSelection(newValue);
  };
  // only add the preselected user if they exist
  // and they don't exist in the initial users list
  // otherwise they will appear twice or MUI will throw a warning
  const selectedUserList =
    selectedUser && !users.some((u) => u.id === selectedUserId)
      ? [selectedUser]
      : [];
  const availableUsers = [...selectedUserList, ...users];

  const renderProfileLink =
    selectedUserId &&
    !disabled &&
    selectedUserId !== selfId &&
    showViewProfileButton;

  return (
    <StyledAutoCompleteContainer>
      {label && (
        <Text
          component="label"
          kind={TextKind.LabelL}
          htmlFor={`memberAutoComplete-${name}`}
        >
          {label}
        </Text>
      )}
      <StyledMuiAutocomplete
        data-testid="autocomplete-search"
        id={`memberAutoComplete-${name}`}
        options={loading ? [] : availableUsers}
        getOptionLabel={(option) =>
          `${getMemberName(option)}${option.id === selfId ? ' (Me)' : ''}`
        }
        getOptionKey={(option) => option.id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedUser}
        onChange={(_, newValue) => handleSelection(newValue)}
        onOpen={() => handleOpen()}
        slotProps={{ clearIndicator: { sx: { visibility: 'visible' } } }}
        loading={loading}
        onFocus={handleFocus}
        disabled={disabled}
        filterOptions={(x) => x} // disable built in filtering: https://mui.com/material-ui/react-autocomplete/#search-as-you-type
        renderInput={(params) => (
          <StyledMuiTextField
            {...params}
            disabled={disabled}
            placeholder={placeholder ?? `Select a member`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (onInputChange) {
                onInputChange(e.target.value);
              }
            }}
            InputProps={{
              ...params.InputProps,
              disabled,
              endAdornment: (
                <>
                  {renderProfileLink && (
                    <Tooltip content="View Profile">
                      <Link
                        to={`/app/members/${selectedUserId}`}
                        target="_blank"
                        buttonVariant={ButtonVariant.Transparent}
                        buttonSize={ButtonSize.Small}
                        testId="memberAutocomplete-viewProfile"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (trackingData) {
                            track(EventNames.MemberClick, {
                              event_label: 'Chat Header Member Link',
                              member_id: selectedUserId,
                              ...trackingData,
                            });
                          }
                        }}
                        icon={
                          <FontAwesomeIcon
                            icon={faUser}
                            color={ICON_SECONDARY}
                          />
                        }
                      />
                    </Tooltip>
                  )}
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),

              startAdornment: selectedUser ? (
                <Avatar
                  user={selectedUser}
                  size={24}
                  sx={{ marginLeft: '8px' }}
                />
              ) : null,
            }}
          />
        )}
        loadingText={
          <Text testId="memberAutocomplete-loadingText" kind={TextKind.BodyM}>
            Loading...
          </Text>
        }
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <li
              key={key}
              style={{
                padding: '8px',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
              {...otherProps}
            >
              <Avatar user={option} size={24} />
              <Text kind={TextKind.BodyM}>
                {getMemberName(option)}
                {option.id === selfId ? ' (Me)' : ''}
              </Text>
            </li>
          );
        }}
      />

      {name && <Errors name={name} errorTextKind={TextKind.BodyS} />}
    </StyledAutoCompleteContainer>
  );
};
